import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchWorkflows, forceRefreshWorkflows, toggleWorkflowStatus, deleteWorkflowById, pauseWorkflow, resumeWorkflow, createWorkflow, updateWorkflow } from "./workflow.thunk";
import { Workflow } from "@grudder/types/workflow/workflow.types";

export interface WorkflowsState {
	items: Workflow[];
	loading: boolean;
	error: string | null;
	lastFetched: number | null;
	updatingWorkflowId: string | null; // Track which workflow is being updated (pause/resume)
	deletingWorkflowId: string | null; // Track which workflow is being deleted
}

const initialState: WorkflowsState = {
	items: [],
	loading: false,
	error: null,
	lastFetched: null,
	updatingWorkflowId: null,
	deletingWorkflowId: null,
};

const workflowsSlice = createSlice({
	name: "workflows",
	initialState,
	reducers: {
		setWorkflows: (state, action: PayloadAction<Workflow[]>) => {
			state.items = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchWorkflows.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchWorkflows.fulfilled, (state, action) => {
				state.loading = false;
				state.items = action.payload;
				state.lastFetched = Date.now();
			})
			.addCase(fetchWorkflows.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.error.message as string) || "Failed to fetch workflows";
			})
			.addCase(forceRefreshWorkflows.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(forceRefreshWorkflows.fulfilled, (state, action) => {
				state.loading = false;
				state.items = action.payload;
				state.lastFetched = Date.now();
			})
			.addCase(forceRefreshWorkflows.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.error.message as string) || "Failed to fetch workflows";
			})
			.addCase(toggleWorkflowStatus.pending, (state) => {
				state.loading = true;
			})
			.addCase(toggleWorkflowStatus.fulfilled, (state, action) => {
				state.loading = false;
				const { id, isActive } = action.payload;
				state.items = state.items.map((w) => (w.id === id ? { ...w, isActive } : w));
			})
			.addCase(toggleWorkflowStatus.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.error.message as string) || "Failed to toggle workflow";
			})
			.addCase(deleteWorkflowById.pending, (state, action) => {
				state.deletingWorkflowId = action.meta.arg;
			})
			.addCase(deleteWorkflowById.fulfilled, (state, action) => {
				state.deletingWorkflowId = null;
				const id = action.payload;
				state.items = state.items.filter((w) => w.id !== id);
			})
			.addCase(deleteWorkflowById.rejected, (state, action) => {
				state.deletingWorkflowId = null;
				state.error = (action.error.message as string) || "Failed to delete workflow";
			})
			.addCase(pauseWorkflow.pending, (state, action) => {
				state.updatingWorkflowId = action.meta.arg.id;
			})
			.addCase(pauseWorkflow.fulfilled, (state, action) => {
				state.updatingWorkflowId = null;
				const { id, isActive } = action.payload;
				state.items = state.items.map((w) => (w.id === id ? { ...w, isActive } : w));
			})
			.addCase(pauseWorkflow.rejected, (state, action) => {
				state.updatingWorkflowId = null;
				state.error = (action.error.message as string) || "Failed to pause workflow";
			})
			.addCase(resumeWorkflow.pending, (state, action) => {
				state.updatingWorkflowId = action.meta.arg.id;
			})
			.addCase(resumeWorkflow.fulfilled, (state, action) => {
				state.updatingWorkflowId = null;
				const { id, isActive } = action.payload;
				state.items = state.items.map((w) => (w.id === id ? { ...w, isActive } : w));
			})
			.addCase(resumeWorkflow.rejected, (state, action) => {
				state.updatingWorkflowId = null;
				state.error = (action.error.message as string) || "Failed to resume workflow";
			});
			// Create workflow
			builder
				.addCase(createWorkflow.pending, (state) => {
					state.loading = true;
					state.error = null;
				})
				.addCase(createWorkflow.fulfilled, (state, _action) => {
					state.loading = false;
					// After create, we can optimistically append or rely on list refresh outside
					// Here we no-op to avoid stale mapping conflicts; consumer can call forceRefresh
				})
				.addCase(createWorkflow.rejected, (state, action) => {
					state.loading = false;
					state.error = (action.payload as string) || (action.error.message as string) || "Failed to create workflow";
				});

			// Update workflow
			builder
				.addCase(updateWorkflow.pending, (state) => {
					state.loading = true;
					state.error = null;
				})
				.addCase(updateWorkflow.fulfilled, (state) => {
					state.loading = false;
				})
				.addCase(updateWorkflow.rejected, (state, action) => {
					state.loading = false;
					state.error = (action.payload as string) || (action.error.message as string) || "Failed to update workflow";
				});
	},
});

export const { setWorkflows } = workflowsSlice.actions;
export default workflowsSlice.reducer;

